import React, { useEffect, useMemo } from 'react';

import useQueryBankAccountStatusesHook from '@fd/customHooks/useQueryBankAccountStatusesHook';
import { BankAccountSummary, StripeConnectedAccount } from '@flipdish/api-client-typescript';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { Translate } from 'react-localize-redux';

import { getSalesChannelType } from '@fd/ui/utils/generateSalesChannelsTypes';

import { useAppSelector } from '../../../../custom-hooks/useAppSelector';
import { useTracking } from '../../../../services/amplitude/useTracking';
import { flagService } from '../../../../services/flagService';
import useStoreToSalesChannelTransformerWithQuery from '../../../Settings/utils/useStoreToSalesChannelTransformerWithQuery';
import StoreListTruncated from '../../../StoreListTruncated';
import { AssignRejectionReason } from './RejectionReason';

const useStyles = makeStyles(() => ({
  listItem: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  listItemRejected: {
    color: '#ff395f',
  },
  listItemAlert: {
    color: '#d96300',
  },
  listItemVerified: {
    color: '#33aa73',
  },
  additionalInfoCaption: {
    width: '100%',
  },
  additionalInfoMessage: {
    color: '#d96300',
  },
  payoutFailedMessage: {
    color: '#ff395f',
  },
  helpdeskArticleLink: {
    color: '#05149e',
    cursor: 'pointer',
    marginLeft: '0.35em',
    whiteSpace: 'nowrap',
  },
  listItemButton: {
    '&:hover': {
      backgroundColor: '#eaf2ff',
    },
    '&:focus': {
      backgroundColor: '#eaf2ff',
    },
  },
  bankingHeader: {
    lineHeight: '0.5px',
  },
  storesTruncated: {
    color: '#05149e',
    fontSize: '12px',
    padding: 0,
    textTransform: 'capitalize',
  },
  infoSmall: {
    width: '13px',
    height: '13px',
    color: 'rgba(0, 0, 0, 0.54)',
    overflow: 'visible',
    alignItems: 'center',
    backgroundColor: 'inherit',
    marginLeft: '2px',
    verticalAlign: 'middle',
  },
}));

/** Company verification troubleshooting — used for “additional information required” on bank accounts. */
const additionalInformationHelpdeskArticleUrl =
  'https://help.flipdish.com/en/articles/9585715-company-verification-trouble-shooting-quick-guide';

/** How and when payouts are issued — used for the “last payout failed” warning on bank accounts. */
const payoutFailedHelpdeskArticleUrl =
  'https://help.flipdish.com/articles/9585397-how-and-when-are-flipdish-payouts-issued?q=payout+failed';

/** Inline "Helpdesk article" link shared by the bank account warning messages. */
const HelpdeskArticleLink: React.FC<{ url: string }> = ({ url }) => {
  const classes = useStyles();
  const openArticle = () => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <Typography
      aria-label="Helpdesk article"
      className={classes.helpdeskArticleLink}
      component="span"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        openArticle();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          openArticle();
        }
      }}
      role="link"
      tabIndex={0}
      variant="caption"
    >
      <Translate id={'Helpdesk_article'} />
    </Typography>
  );
};

export interface BankingAccount {
  account: BankAccountSummary;
}

type Props = BankingAccount;
const AccountDetails: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { account } = props;
  const bankStateEnum = BankAccountSummary.AccountStateEnum;
  const stripeStateEnum = StripeConnectedAccount.AccountStatusEnum;
  const bankAccountState = account.AccountState || {};
  const stripeAccountState = account.StripeConnectedAccountInfo
    ? account.StripeConnectedAccountInfo.AccountStatus
    : {};
  const businessType = account.BusinessType || '';
  const accountName = account.AccountName || '';
  const accountRejectReason = account.RejectionReason || '';
  const iban = account.Iban || '';
  const classes = useStyles();

  const isPayoutFailedBannerOn = useAppSelector((state) =>
    flagService.isFlagOn(state, 'payout_failed_banner')
  );
  const orgId = useAppSelector((state) => state.currentApp?.OrgId ?? '');
  const brandId = useAppSelector((state) => state.currentApp?.AppId ?? '');
  const isFlipdishGlobal = brandId === 'flipdish-global';
  const { hasFailedPayoutForStripeId } = useQueryBankAccountStatusesHook(
    orgId,
    brandId,
    isPayoutFailedBannerOn && !isFlipdishGlobal
  );
  // Match this account's Stripe id against the bank-account status API's accountId.
  const hasFailedPayout =
    isPayoutFailedBannerOn &&
    !isFlipdishGlobal &&
    hasFailedPayoutForStripeId(account.StripeConnectedAccountInfo?.StripeId);

  const storeIdAssociations = useStoreToSalesChannelTransformerWithQuery();

  const accountStoreNames = useMemo(() => {
    const associatedStoreNames = account?.StoreIds?.map((storeId) => {
      const association = storeIdAssociations.find(
        (association) => association.storeId === storeId
      );
      return association?.salesChannelName
        ? `${association.salesChannelName}${getSalesChannelType(
            association.salesChannelType ?? ''
          )}`
        : '';
    });
    // edge case where there is no association for the store
    const removedEmptyStoreNames = associatedStoreNames?.filter((name) => name);
    if ((removedEmptyStoreNames || [])?.length > 0) {
      return removedEmptyStoreNames;
    }
    return account?.StoreNames || [];
  }, [account, storeIdAssociations]);

  const getStatusfromAccountState = () => {
    switch (true) {
      case bankAccountState === bankStateEnum.Unverified ||
        stripeAccountState === stripeStateEnum.Disabled:
        return stripeStateEnum.Disabled;
      case stripeAccountState === stripeStateEnum.Rejected:
        return stripeStateEnum.Rejected;
      case (bankAccountState === bankStateEnum.Verified ||
        bankAccountState === bankStateEnum.AwatingVerification ||
        bankAccountState === bankStateEnum.Grandfathered) &&
        stripeAccountState === stripeStateEnum.UpdateExternalAccount:
        return stripeStateEnum.UpdateExternalAccount;
      case stripeAccountState === stripeStateEnum.AdditionalInformationRequired:
        return stripeStateEnum.AdditionalInformationRequired;
      case stripeAccountState === stripeStateEnum.Unverified || businessType === '':
        return stripeStateEnum.Unverified;
      case stripeAccountState === stripeStateEnum.PendingVerification ||
        bankAccountState === bankStateEnum.AwatingVerification:
        return stripeStateEnum.PendingVerification;
      case bankAccountState === bankStateEnum.Verified ||
        stripeAccountState === stripeStateEnum.Enabled ||
        bankAccountState === bankStateEnum.Grandfathered:
        return stripeStateEnum.Enabled;
      default:
        return stripeStateEnum.PendingVerification;
    }
  };

  const getTitleClass = () => {
    return bankAccountState === bankStateEnum.Unverified ||
      stripeAccountState === stripeStateEnum.Disabled
      ? classes.listItemRejected
      : '';
  };

  const getbankingSubtitle = () => {
    const accountDetailsStatus = getStatusfromAccountState();

    switch (accountDetailsStatus) {
      case stripeStateEnum.Disabled:
      case stripeStateEnum.Rejected:
        return (
          <>
            <Typography color="secondary" variant="caption">
              {accountRejectReason && (
                <AssignRejectionReason rejectionReason={accountRejectReason} />
              )}
            </Typography>
            <br />
            <Typography color="secondary" variant="caption">
              <Translate id="Please_try_to_add_the_bank_account_again" />
            </Typography>
          </>
        );
      case stripeStateEnum.PendingVerification:
        return (
          <Typography variant="caption">
            <Translate id="Pending_verification_we_will_notify_you" />
          </Typography>
        );
      case stripeStateEnum.AdditionalInformationRequired: {
        return (
          <Typography component="div" variant="caption" className={classes.additionalInfoCaption}>
            <span className={classes.additionalInfoMessage}>
              <Translate id="You_must_provide_additional_information" />
            </span>{' '}
            <HelpdeskArticleLink url={additionalInformationHelpdeskArticleUrl} />
          </Typography>
        );
      }
      case stripeStateEnum.Unverified:
        return (
          <Typography variant="caption" className={classes.listItemAlert}>
            <Translate id="verification_required_to_receive_payouts" />
          </Typography>
        );
      case stripeStateEnum.UpdateExternalAccount:
        return (
          <Typography variant="caption" className={classes.listItemAlert}>
            <Translate id="Please_update_your_banking_details_for_this_account" />
          </Typography>
        );
      case stripeStateEnum.Enabled:
        return (
          <Typography variant="caption" className={classes.listItemVerified}>
            <Translate id="Verification_complete" />
          </Typography>
        );
      default:
        return null;
    }
  };

  const getPayoutFailedWarning = () => {
    if (!hasFailedPayout) {
      return null;
    }
    return (
      <Typography component="div" variant="caption" className={classes.additionalInfoCaption}>
        <span className={classes.payoutFailedMessage}>
          <Translate id="Payout_failed_contact_your_bank" />
        </span>{' '}
        <HelpdeskArticleLink url={payoutFailedHelpdeskArticleUrl} />
      </Typography>
    );
  };

  const { trackEvent } = useTracking();

  useEffect(() => {
    if (typeof stripeAccountState !== 'object' && stripeAccountState) {
      trackEvent('portal_stripe_status', {
        status: `${stripeAccountState}`,
      });
    }
  }, []);

  return (
    <ListItemText
      primary={
        <div className={classes.bankingHeader} data-fd="account-details-list">
          <Typography variant="subtitle1" className={getTitleClass()}>
            {accountName}
          </Typography>
          <Typography variant="caption" className={getTitleClass()}>
            <>{iban} </>
          </Typography>
          {accountStoreNames && (
            <StoreListTruncated
              storeNames={accountStoreNames}
              textPrefix={true}
              variant={'caption'}
              btnFontSize={'12px'}
              minWidth={'small'}
              color="textSecondary"
            />
          )}
        </div>
      }
      secondaryTypographyProps={{ component: 'div' }}
      secondary={
        <>
          {getbankingSubtitle()}
          {getPayoutFailedWarning()}
        </>
      }
    />
  );
};

export default AccountDetails;
